const supplementalResources = [
    {
        category: 'Publication',
        title: 'Disassembling the Trust Machine (Brekke, 2019)',
        description: 'Durham accepted-version thesis on blockchain imaginaries, political economy, and trust infrastructures.',
        links: [
            { label: 'PDF', url: 'https://permet.co/content/files/2026/07/DisassemblingTrustMachine_Brekke2019.pdf' },
            { label: 'Source', url: 'https://etheses.durham.ac.uk/id/eprint/13174/1/DisassemblingTrustMachine_JKBrekke_2019.pdf' }
        ]
    },
    {
        category: 'Protocol',
        title: 'Arweave',
        description: 'Permanent data storage for publication artifacts and long-term provenance records.',
        links: [
            { label: 'Site', url: 'https://www.arweave.org/' },
            { label: 'Docs', url: 'https://docs.arweave.org/' }
        ]
    },
    {
        category: 'Standards',
        title: 'GS1 Digital Link',
        description: 'Open identifier standard used to connect physical products to verified web data.',
        links: [
            { label: 'Standard', url: 'https://www.gs1.org/standards/gs1-digital-link' },
            { label: 'Guide', url: 'https://ref.gs1.org/standards/digital-link/' }
        ]
    },
    {
        category: 'Standards',
        title: 'W3C Verifiable Credentials',
        description: 'Credential model for portable attestations used across traceability systems.',
        links: [
            { label: 'Spec', url: 'https://www.w3.org/TR/vc-data-model/' },
            { label: 'W3C', url: 'https://www.w3.org/TR/vc-overview/' }
        ]
    },
    {
        category: 'Software',
        title: 'OpenZeppelin Contracts',
        description: 'Security-audited smart contract library for production Solidity applications.',
        links: [
            { label: 'Docs', url: 'https://docs.openzeppelin.com/contracts' },
            { label: 'GitHub', url: 'https://github.com/OpenZeppelin/openzeppelin-contracts' }
        ]
    },
    {
        category: 'Publication',
        title: 'Mirror',
        description: 'Web3-native publishing platform for long-form writing and onchain drops.',
        links: [
            { label: 'Site', url: 'https://mirror.xyz/' },
            { label: 'About', url: 'https://paragraph.xyz/@mirror' }
        ]
    }
];

const resourceTitle = (card) => {
    const titleNode = card.querySelector('h4 a, h4, .res-index-card__title');
    return (titleNode?.textContent || '').trim().toLowerCase();
};

const parseCard = (card) => {
    const category = (card.querySelector('.res_card_category span')?.textContent || 'Archive').trim();
    const titleNode = card.querySelector('h4 a, h4, .res-index-card__title');
    const titleLink = card.querySelector('h4 a, .res-index-card__link');

    return {
        category,
        title: (titleNode?.textContent || '').trim(),
        titleUrl: titleLink?.href || '#',
        description: (card.querySelector('p, .res-index-card__text')?.textContent || '').trim(),
        links: [...card.querySelectorAll('.cardlinks a, .res-index-card__foot a')].map((anchor) => ({
            label: anchor.textContent.replace(/^\[|\]$/g, '').trim(),
            url: anchor.href
        }))
    };
};

const buildSupplementalCard = (resource) => {
    const card = document.createElement('div');
    card.classList.add('res_card');

    const categoryWrap = document.createElement('div');
    categoryWrap.classList.add('res_card_category');
    const category = document.createElement('span');
    category.textContent = resource.category;
    categoryWrap.appendChild(category);

    const title = document.createElement('h4');
    const titleLink = document.createElement('a');
    titleLink.href = resource.links[0].url;
    titleLink.target = '_blank';
    titleLink.rel = 'noopener noreferrer';
    titleLink.textContent = resource.title;
    title.appendChild(titleLink);

    const text = document.createElement('p');
    text.textContent = resource.description;

    const links = document.createElement('div');
    links.classList.add('cardlinks');
    resource.links.forEach((link) => {
        const anchor = document.createElement('a');
        anchor.href = link.url;
        anchor.target = '_blank';
        anchor.rel = 'noopener noreferrer';
        anchor.textContent = `[${link.label}]`;
        links.appendChild(anchor);
    });

    card.appendChild(categoryWrap);
    card.appendChild(title);
    card.appendChild(text);
    card.appendChild(links);
    return card;
};

const buildIndexCard = (data, index) => {
    const card = document.createElement('article');
    card.className = 'res-index-card res_card';
    card.dataset.category = data.category;
    card.setAttribute('tabindex', '0');

    const ref = String(index + 1).padStart(3, '0');

    const link = document.createElement('a');
    link.className = 'res-index-card__link';
    link.href = data.titleUrl;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    link.title = data.title;

    const sheet = document.createElement('div');
    sheet.className = 'res-index-card__sheet';

    const head = document.createElement('header');
    head.className = 'res-index-card__head';
    head.innerHTML = `<span>${data.category}</span><span>${ref}</span>`;

    const panel = document.createElement('div');
    panel.className = 'res-index-card__panel';
    const title = document.createElement('h2');
    title.className = 'res-index-card__title';
    title.textContent = data.title;
    const text = document.createElement('p');
    text.className = 'res-index-card__text';
    text.textContent = data.description;
    panel.appendChild(title);
    panel.appendChild(text);

    const foot = document.createElement('footer');
    foot.className = 'res-index-card__foot';
    data.links.forEach((item) => {
        const anchor = document.createElement('a');
        anchor.href = item.url;
        anchor.target = '_blank';
        anchor.rel = 'noopener noreferrer';
        anchor.textContent = item.label;
        foot.appendChild(anchor);
    });

    sheet.appendChild(head);
    sheet.appendChild(panel);
    sheet.appendChild(foot);
    link.appendChild(sheet);
    card.appendChild(link);
    return card;
};

const buildFilterBar = (categories, stack) => {
    const filterBar = document.createElement('div');
    filterBar.className = 'res-filter';
    filterBar.setAttribute('role', 'toolbar');
    filterBar.setAttribute('aria-label', 'Filter resources by theme');

    const buttons = [{ label: 'All', value: 'all' }, ...categories.map((cat) => ({ label: cat, value: cat.toLowerCase() }))];

    buttons.forEach(({ label, value }) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = `res-filter__btn${value === 'all' ? ' is-active' : ''}`;
        button.dataset.filter = value;
        button.textContent = label;
        filterBar.appendChild(button);
    });

    filterBar.addEventListener('click', (event) => {
        const button = event.target.closest('.res-filter__btn');
        if (!button) return;

        filterBar.querySelectorAll('.res-filter__btn').forEach((item) => {
            item.classList.remove('is-active');
        });
        button.classList.add('is-active');

        const filter = button.dataset.filter;
        stack.querySelectorAll('.res-index-card').forEach((card) => {
            const match = filter === 'all' || card.dataset.category.toLowerCase() === filter;
            card.hidden = !match;
        });
    });

    return filterBar;
};

const renderResources = () => {
    const resCards = document.querySelectorAll('.res_card') || [];
    const ghContent = document.getElementById('gh_content');
    if (resCards.length === 0 || !ghContent) return;

    ghContent.closest('.gh-article')?.classList.add('permet-resources-page');

    const cards = [...resCards];
    const existingTitles = new Set(cards.map(resourceTitle).filter(Boolean));
    supplementalResources.forEach((resource) => {
        const key = resource.title.trim().toLowerCase();
        if (existingTitles.has(key)) return;
        cards.push(buildSupplementalCard(resource));
        existingTitles.add(key);
    });

    const parsed = cards.map(parseCard);
    const categories = [...new Set(parsed.map((item) => item.category))].sort();

    resCards.forEach((el) => {
        el.remove();
    });

    ghContent.querySelector('.res-resources-board')?.remove();

    const board = document.createElement('div');
    board.className = 'res-resources-board';

    const stack = document.createElement('div');
    stack.className = 'res-index-stack';

    parsed.forEach((data, index) => {
        stack.appendChild(buildIndexCard(data, index));
    });

    if (categories.length > 1) {
        board.appendChild(buildFilterBar(categories, stack));
    }

    board.appendChild(stack);
    ghContent.insertAdjacentElement('beforeend', board);
};

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', renderResources);
} else {
    renderResources();
}
